import { useMemo } from 'react';

import { Checkbox, Segmented, Table, Tooltip } from 'antd';
import { ColumnType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';

import SettingCard from 'src/pages/Devices/DeviceDetails/TabPages/SensorSettings/components/SettingCard';

import {
  CUSTOM_PERSON_FIELD_TYPE,
  MAX_SITE_CUSTOM_PERSON_FIELDS,
  PHONE_NUMBER_PERSON_FIELD_TYPE,
  SiteFormValues,
} from '../../config';
import { useSiteDetails } from '../../SiteDetailsContext';
import { LabelText } from './PersonFields.styled';

type PersonField = NonNullable<SiteFormValues['personFields']>[number];

interface PersonFieldRow {
  id: string;
  type: PersonField['type'];
  key?: string;
  label: string;
}

const isSameField = (personField: PersonField, row: PersonFieldRow) =>
  personField.type === row.type && (row.type === PHONE_NUMBER_PERSON_FIELD_TYPE || personField.key === row.key);

/**
 * Picks which person fields this Enrollment Site collects. The list is built from the client's custom person fields
 * rather than from the site, so deleting one at the client level drops it off here and off the site with nothing to
 * clean up by hand.
 */
export const PersonFields = () => {
  const { t } = useTranslation();
  const { formMethods, clientCustomPersonFields } = useSiteDetails();

  const personFields = formMethods.watch('personFields') ?? [];

  const rows: PersonFieldRow[] = useMemo(
    () => [
      {
        id: PHONE_NUMBER_PERSON_FIELD_TYPE,
        type: PHONE_NUMBER_PERSON_FIELD_TYPE,
        label: t('site.personFields.phoneNumber'),
      },
      ...clientCustomPersonFields.map((field) => ({
        id: `${CUSTOM_PERSON_FIELD_TYPE}:${field.key}`,
        type: CUSTOM_PERSON_FIELD_TYPE as PersonField['type'],
        key: field.key,
        label: field.label,
      })),
    ],
    [t, clientCustomPersonFields],
  );

  const selectedCustomFieldCount = personFields.filter((field) => field.type === CUSTOM_PERSON_FIELD_TYPE).length;
  const isAtCustomFieldLimit = selectedCustomFieldCount >= MAX_SITE_CUSTOM_PERSON_FIELDS;

  const setPersonFields = (next: PersonField[]) =>
    formMethods.setValue('personFields', next, { shouldDirty: true, shouldValidate: true });

  const handleToggle = (row: PersonFieldRow, isSelected: boolean) => {
    if (isSelected) {
      setPersonFields([...personFields, { type: row.type, key: row.key, required: false }]);
      return;
    }

    setPersonFields(personFields.filter((personField) => !isSameField(personField, row)));
  };

  const handleRequiredChange = (row: PersonFieldRow, required: boolean) =>
    setPersonFields(
      personFields.map((personField) => (isSameField(personField, row) ? { ...personField, required } : personField)),
    );

  const columns: ColumnType<PersonFieldRow>[] = useMemo(
    () => [
      {
        title: '',
        key: 'selected',
        width: 48,
        render: (_: unknown, row) => {
          const isSelected = personFields.some((personField) => isSameField(personField, row));
          const isBlockedByLimit = !isSelected && row.type === CUSTOM_PERSON_FIELD_TYPE && isAtCustomFieldLimit;

          return (
            <Tooltip
              title={
                isBlockedByLimit
                  ? t('site.personFields.maxFieldsTooltip', { max: MAX_SITE_CUSTOM_PERSON_FIELDS })
                  : undefined
              }
            >
              <span>
                <Checkbox
                  checked={isSelected}
                  disabled={isBlockedByLimit}
                  aria-label={row.label}
                  data-testid={`person-field-checkbox-${row.id}`}
                  onChange={(event) => handleToggle(row, event.target.checked)}
                />
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: t('site.personFields.field'),
        key: 'label',
        render: (_: unknown, row) => <LabelText>{row.label}</LabelText>,
      },
      {
        title: t('site.personFields.requirement'),
        key: 'requirement',
        align: 'right',
        width: 220,
        render: (_: unknown, row) => {
          const selected = personFields.find((personField) => isSameField(personField, row));

          return (
            <Segmented
              value={selected?.required ? 'required' : 'optional'}
              disabled={!selected}
              data-testid={`person-field-requirement-${row.id}`}
              options={[
                { label: t('site.personFields.optional'), value: 'optional' },
                { label: t('site.personFields.required'), value: 'required' },
              ]}
              onChange={(value) => handleRequiredChange(row, value === 'required')}
            />
          );
        },
      },
    ],
    [t, personFields, isAtCustomFieldLimit],
  );

  return (
    <SettingCard
      title={t('site.personFields.title')}
      description={t('site.personFields.description', { max: MAX_SITE_CUSTOM_PERSON_FIELDS })}
      data-testid="site-person-fields-card"
      items={[
        {
          content: (
            <Table
              dataSource={rows}
              columns={columns}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              data-testid="person-fields-table"
            />
          ),
        },
      ]}
    />
  );
};
